import React, { useEffect, useRef, useState } from "react";
import api from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Loader2, MapPin, Check, Clock } from "lucide-react";

const RECENT_KEY = "recent_cities_v1";
const readRecent = () => {
  try { return JSON.parse(localStorage.getItem(RECENT_KEY) || "[]"); }
  catch (err) { console.warn("recent-cities read failed:", err?.message); return []; }
};
const saveRecent = (city) => {
  try {
    const cur = readRecent().filter(c => c.id !== city.id);
    cur.unshift({ id: city.id, name: city.name, label: city.label, lat: city.lat, lon: city.lon, timezone: city.timezone, country: city.country });
    localStorage.setItem(RECENT_KEY, JSON.stringify(cur.slice(0, 5)));
  } catch (err) { console.warn("recent-cities save failed:", err?.message); }
};

/**
 * Global city autocomplete backed by /api/geo/cities (34,000+ cities).
 * Also surfaces recently picked cities when the input is focused with an empty/short query.
 */
export default function CitySearchInput({ value, onChange, onSelect, placeholder = "Type a city…", className = "", testid = "city-search" }) {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [active, setActive] = useState(0);
  const [picked, setPicked] = useState(false);
  const [userTyped, setUserTyped] = useState(false);
  const [recent, setRecent] = useState(() => readRecent());
  const debounceRef = useRef();
  const boxRef = useRef();

  const showingRecent = (!value || value.length < 2) && !picked && recent.length > 0;
  const list = showingRecent ? recent : items;

  useEffect(() => {
    if (!value || value.length < 2 || picked || !userTyped) {
      setItems([]);
      return;
    }
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(async () => {
      setLoading(true);
      try {
        const { data } = await api.get("/geo/cities", { params: { q: value, limit: 10 } });
        setItems(data.results || []);
        setOpen((data.results || []).length > 0);
        setActive(0);
      } finally { setLoading(false); }
    }, 220);
    return () => clearTimeout(debounceRef.current);
  }, [value, picked, userTyped]);

  useEffect(() => {
    const onDoc = (e) => { if (boxRef.current && !boxRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, []);

  const choose = (c) => {
    setPicked(true);
    saveRecent(c);
    setRecent(readRecent());
    onChange?.(c.label);
    onSelect?.(c);
    setOpen(false);
  };

  const onFocus = () => {
    if (showingRecent) { setOpen(true); setActive(0); }
    else if (items.length > 0 && value?.length >= 2) setOpen(true);
  };

  const onKey = (e) => {
    if (!open || !list.length) return;
    if (e.key === "ArrowDown") { e.preventDefault(); setActive(i => Math.min(list.length - 1, i + 1)); }
    else if (e.key === "ArrowUp") { e.preventDefault(); setActive(i => Math.max(0, i - 1)); }
    else if (e.key === "Enter") { e.preventDefault(); choose(list[active]); }
    else if (e.key === "Escape") setOpen(false);
  };

  return (
    <div ref={boxRef} className={`relative ${className}`}>
      <div className="relative">
        <MapPin className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" />
        <Input
          value={value || ""}
          onChange={(e) => { setPicked(false); setUserTyped(true); onChange?.(e.target.value); }}
          onFocus={onFocus}
          onKeyDown={onKey}
          placeholder={placeholder}
          className="pl-9 pr-9 h-11 rounded-xl"
          data-testid={testid}
          autoComplete="off"
        />
        {loading && <Loader2 className="w-4 h-4 absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 animate-spin" />}
      </div>
      {open && list.length > 0 && (
        <div className="absolute z-50 mt-1 w-full max-h-72 overflow-y-auto rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 shadow-lg" data-testid={`${testid}-dropdown`}>
          {showingRecent && (
            <div className="px-3 py-1.5 text-[10px] uppercase tracking-widest text-slate-500 border-b border-slate-100 dark:border-slate-800 flex items-center gap-1">
              <Clock className="w-3 h-3" /> Recent
            </div>
          )}
          {list.map((c, i) => (
            <button
              key={c.id + "-" + i}
              type="button"
              onClick={() => choose(c)}
              onMouseEnter={() => setActive(i)}
              className={`w-full text-left px-3 py-2 flex items-center gap-2 text-sm border-b last:border-b-0 border-slate-100 dark:border-slate-800 ${i === active ? "bg-amber-50 dark:bg-amber-500/10" : "hover:bg-slate-50 dark:hover:bg-slate-800/70"}`}
              data-testid={`${testid}-item-${i}`}
            >
              <MapPin className="w-3.5 h-3.5 text-amber-500 shrink-0" />
              <div className="min-w-0 flex-1">
                <div className="font-medium truncate">{c.name}</div>
                <div className="text-[10px] text-slate-500 font-mono truncate">{c.country} · {c.timezone || "auto"} · {c.lat?.toFixed?.(2)}, {c.lon?.toFixed?.(2)}</div>
              </div>
              {i === active && <Check className="w-3.5 h-3.5 text-amber-500" />}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
